/**
 * JWT token utilities
 * Functions for token parsing, validation, and service configuration
 */

import OrganisationManagementService from '@/services/OrganisationManagementService'
import AccommodationService from '@/services/accommodationService/AccommodationService'
import RateInsightsService from '@/services/accommodationService/RateInsightsService'
import RateConnectService from '@/services/premiumService/RateConnectService'
import PremiumService from '@/services/premiumService/PremiumService'
import DashboardService from '@/services/DashboardService'
import PropertyDetailsService from '@/services/PropertyDetailsService'
import AdminService from '@/services/AdminService'
import HotelOffersService from '@/services/premiumService/HotelOffersService'
import SmartBoostService from '@/services/premiumService/SmartBoostService'

/** JWT payload structure */
export interface JwtPayload {
  exp?: number
  organisation_id?: number
  sub?: string
  [key: string]: unknown
}

/** Studio token structure from localStorage */
export interface StudioToken {
  access_token: string
  expires_in: number
  token_type?: string
  refresh_token?: string
}


/**
 * Extract and parse payload from JWT token
 * @param token - JWT token string
 * @returns Decoded JWT payload or undefined if invalid
 */
export function getPayloadFromJwt(token: string | null | undefined): JwtPayload | undefined {
  if (!token) return undefined

  const encodedPayload = token.split('.')?.[1]
  if (!encodedPayload) return undefined

  try {
    const decodedPayload = JSON.parse(window.atob(encodedPayload))
    return decodedPayload as JwtPayload
  } catch {
    return undefined
  }
}

/**
 * Retrieve token from localStorage
 * @param tokenName - Name of the token key in localStorage
 * @returns Parsed token object or null
 */
export function getTokenFromLocalStorage(tokenName = ''): StudioToken | null {
  try {
    const result = window.localStorage.getItem(tokenName)

    if (result) {
      return JSON.parse(result) as StudioToken
    }
    return null
  } catch {
    return null
  }
}

/**
 * Check if studio token is valid (not expired)
 * @param token - Studio token object
 * @returns True if token is valid and not expired
 */
export function isTokenValid(token: StudioToken | null | undefined): boolean {
  if (!token) return false
  if (!token.expires_in || !token.access_token) return false

  const payload = getPayloadFromJwt(token.access_token)

  if (!payload?.exp) return false

  // Check if token expires in next 6 minutes (360000ms)
  const expiresIn = payload.exp * 1000
  const isTokenExpired = Date.now() > expiresIn - 360000

  return !isTokenExpired
}

/**
 * Check if token is valid for currently selected organisation
 * @param token - Studio token object
 * @returns True if token belongs to selected organisation
 */
export function isTokenValidForOrg(token: StudioToken | null | undefined): boolean {
  if (!isTokenValid(token)) return false
  const selectedOrgId = window.localStorage.getItem('selected_organisation_id')
  if (!token || !token.access_token || !selectedOrgId) return false

  const payload = getPayloadFromJwt(token.access_token)

  // Check if token belongs to selected organisation
  return String(payload?.organisation_id) === String(selectedOrgId)
}

/**
 * Set studio token in all API services
 * Configures authentication token for service instances
 * @param token - Studio token object
 */
export function setStudioTokenInServices(token: StudioToken | null | undefined): void {
  if (!token) return

  const accessToken = token.access_token

  OrganisationManagementService.setStudioToken(accessToken)
  AccommodationService.setStudioToken(accessToken)
  RateConnectService.setStudioToken(accessToken)
  SmartBoostService.setStudioToken(accessToken)
  PremiumService.setStudioToken(accessToken)
  DashboardService.setStudioToken(accessToken)
  PropertyDetailsService.setStudioToken(accessToken)
  RateInsightsService.setStudioToken(accessToken)
  AdminService.setStudioToken(accessToken)
  HotelOffersService.setStudioToken(accessToken)
}